import { useState, type ReactNode } from 'react';
import {
  CalendarCheck,
  CalendarClock,
  CalendarX,
  Check,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  Hourglass,
  Languages,
  Link2Off,
  Mail,
  MailCheck,
  MessageCircleHeart,
  Smartphone,
} from 'lucide-react';
import { PATIENT_LANGUAGES, type PatientLanguage } from '@/i18n/patient';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import type { PatientLinkRequest, PatientLinkView } from '@/types';

/**
 * The patient link: creating it, and then looking after it.
 *
 * The link opens the crown in a viewer made for looking, not judging - views,
 * zoom, a slow spin, the crown in its shade, AR on a phone. No notes, no
 * parameters, no decision, no lab or clinic details.
 *
 * It has no expiry of its own. The dentist knows when the patient no longer
 * needs it, so the link stays open until they turn it off here - rather than
 * lapsing on a date picked in advance, possibly before the patient ever
 * opened it.
 *
 * Once it exists, this is also where the patient's answer comes back: the
 * dentist has no account, so their own review page is where "confirmed" or
 * "asked for another time" shows up (and it is emailed to them as well).
 */
const SHOWN = [
  'The crown on their jaw, in its shade',
  'Views, zoom, spin and AR',
  'Your message and date',
];
const HIDDEN = ['Notes and pins', 'Parameters', 'Your decision', 'Lab and clinic names'];

const textareaClass =
  'w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-[13px] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40';

function Field({
  id,
  icon,
  label,
  optional = true,
  children,
}: {
  id: string;
  icon: ReactNode;
  label: string;
  optional?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="flex items-center gap-1.5 text-[12px]">
        <span className="text-muted-foreground">{icon}</span>
        {label}
        {optional && <span className="font-normal text-muted-foreground">(optional)</span>}
      </Label>
      {children}
    </div>
  );
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** The patient's answer, as the dentist reads it. */
function AppointmentStatusLine({
  appointment,
}: {
  appointment: NonNullable<PatientLinkView['appointment']>;
}) {
  if (appointment.status === 'confirmed') {
    return (
      <p className="flex items-center gap-2 rounded-md bg-success-50 px-3 py-2 text-[12.5px] text-success-600">
        <CalendarCheck className="size-4 shrink-0" />
        <span>
          <span className="font-semibold">Patient confirmed</span> {when(appointment.at)}
          {appointment.answeredAt && (
            <span className="block text-[11.5px] opacity-80">
              Answered {when(appointment.answeredAt)}
            </span>
          )}
        </span>
      </p>
    );
  }
  if (appointment.status === 'reschedule_requested') {
    return (
      <p className="flex items-center gap-2 rounded-md bg-warning-50 px-3 py-2 text-[12.5px] text-warning-600">
        <CalendarX className="size-4 shrink-0" />
        <span>
          <span className="font-semibold">Patient asked for another time</span> instead of{' '}
          {when(appointment.at)}
          {appointment.answeredAt && (
            <span className="block text-[11.5px] opacity-80">
              Answered {when(appointment.answeredAt)}
            </span>
          )}
        </span>
      </p>
    );
  }
  return (
    <p className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-[12.5px] text-muted-foreground">
      <Hourglass className="size-4 shrink-0" />
      Waiting for the patient to confirm {when(appointment.at)}
    </p>
  );
}

interface SharePatientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The current patient link, if one has been made. */
  patientLink: PatientLinkView | null;
  /** Creates the link; with an email, the server also sends it to the patient. */
  onCreate: (request: PatientLinkRequest) => Promise<void>;
  onTurnOff: () => Promise<void>;
}

export function SharePatientDialog({
  open,
  onOpenChange,
  patientLink,
  onCreate,
  onTurnOff,
}: SharePatientDialogProps) {
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmingOff, setConfirmingOff] = useState(false);
  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState<PatientLanguage>('en');
  const [message, setMessage] = useState('');
  const [appointmentAt, setAppointmentAt] = useState('');
  // A dentist replacing a turned-off link starts from an empty form.
  const [replacing, setReplacing] = useState(false);

  const trimmed = email.trim();
  // Enough to catch a typo before the send, not a full RFC check - the server
  // validates the address properly.
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  const emailBad = trimmed.length > 0 && !emailValid;

  const showForm = !patientLink || replacing;

  const create = async () => {
    if (emailBad) return;
    setCreating(true);
    try {
      await onCreate({
        email: emailValid ? trimmed : null,
        message: message.trim() || null,
        language,
        appointmentAt: appointmentAt ? new Date(appointmentAt).toISOString() : null,
      });
      setReplacing(false);
      setEmail('');
      setMessage('');
      setAppointmentAt('');
    } finally {
      setCreating(false);
    }
  };

  const copy = async () => {
    if (!patientLink) return;
    await navigator.clipboard.writeText(patientLink.url);
    setCopied(true);
    window.setTimeout(() => {
      setCopied(false);
    }, 1800);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          setConfirmingOff(false);
          setReplacing(false);
        }
      }}
    >
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="size-5 text-primary" />
            {showForm ? 'Share with your patient' : 'Your patient link'}
          </DialogTitle>
          <DialogDescription>
            {showForm
              ? 'A view-only link that shows the patient their new crown. It works on any phone, needs no account, and stays open until you turn it off.'
              : 'The patient’s answer to the fitting date shows here when you open this link again.'}
          </DialogDescription>
        </DialogHeader>

        {showForm ? (
          <>
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-border p-3 text-[12px]">
              <div>
                <p className="mb-1 flex items-center gap-1.5 text-[10.5px] font-semibold tracking-wide text-success-600 uppercase">
                  <Eye className="size-3.5" /> They see
                </p>
                <ul className="space-y-0.5">
                  {SHOWN.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="mb-1 flex items-center gap-1.5 text-[10.5px] font-semibold tracking-wide text-muted-foreground uppercase">
                  <EyeOff className="size-3.5" /> They don't see
                </p>
                <ul className="space-y-0.5 text-muted-foreground">
                  {HIDDEN.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                <Field
                  id="patient-email"
                  icon={<Mail className="size-3.5" />}
                  label="Patient's email"
                >
                  <Input
                    id="patient-email"
                    type="email"
                    inputMode="email"
                    autoComplete="off"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                    }}
                    placeholder="patient@example.com"
                    aria-invalid={emailBad}
                  />
                </Field>
                <Field
                  id="patient-language"
                  icon={<Languages className="size-3.5" />}
                  label="Page language"
                  optional={false}
                >
                  <Select
                    value={language}
                    onValueChange={(value) => {
                      setLanguage(value as PatientLanguage);
                    }}
                  >
                    <SelectTrigger id="patient-language" className="w-full text-[13px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper" align="end">
                      {PATIENT_LANGUAGES.map((item) => (
                        <SelectItem key={item.code} value={item.code} className="text-[13px]">
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <p className="-mt-2 text-[11.5px] text-muted-foreground">
                {emailBad
                  ? 'That email address does not look right.'
                  : 'We email them the link. Leave it empty to copy the link yourself.'}
              </p>

              <Field
                id="patient-message"
                icon={<MessageCircleHeart className="size-3.5" />}
                label="Message for the patient"
              >
                <textarea
                  id="patient-message"
                  rows={2}
                  maxLength={500}
                  value={message}
                  onChange={(event) => {
                    setMessage(event.target.value);
                  }}
                  placeholder="This is the crown for your lower right molar."
                  className={textareaClass}
                />
              </Field>

              <Field
                id="patient-appointment"
                icon={<CalendarClock className="size-3.5" />}
                label="Fitting appointment"
              >
                <Input
                  id="patient-appointment"
                  type="datetime-local"
                  value={appointmentAt}
                  onChange={(event) => {
                    setAppointmentAt(event.target.value);
                  }}
                />
                <p className="text-[11.5px] text-muted-foreground">
                  The patient confirms it or asks for another time from the link. You see their
                  answer here and by email.
                </p>
              </Field>
            </div>
          </>
        ) : (
          patientLink && (
            // min-w-0: the dialog is a grid, and a grid item is never narrower
            // than its content - one long unbroken URL widened the whole dialog
            // past the screen.
            <div className="min-w-0 space-y-3">
              {patientLink.active ? (
                <>
                  <div className="flex min-w-0 items-center gap-2 rounded-md border border-border bg-muted/50 py-1 pr-1 pl-3">
                    <span
                      className="min-w-0 flex-1 truncate font-mono text-[12px]"
                      title={patientLink.url}
                    >
                      {patientLink.url}
                    </span>
                    <Button
                      size="sm"
                      variant={copied ? 'secondary' : 'default'}
                      onClick={() => void copy()}
                      className="shrink-0"
                    >
                      {copied ? <Check /> : <Copy />}
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                  <div className="space-y-0.5 text-[11.5px] text-muted-foreground">
                    {patientLink.emailedTo && (
                      <p className="flex items-start gap-1.5">
                        <MailCheck className="mt-px size-3.5 shrink-0" />
                        <span className="min-w-0 wrap-anywhere">
                          Emailed to {patientLink.emailedTo}
                        </span>
                      </p>
                    )}
                    <p>Created {when(patientLink.createdAt)} · open until you turn it off</p>
                  </div>
                </>
              ) : (
                <p className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-[12.5px] text-muted-foreground">
                  <Link2Off className="size-4 shrink-0" />
                  This link is turned off. The patient sees a message saying so.
                </p>
              )}

              {patientLink.appointment ? (
                <AppointmentStatusLine appointment={patientLink.appointment} />
              ) : (
                <p className="text-[12px] text-muted-foreground">
                  No fitting date was sent with this link.
                </p>
              )}

              {patientLink.active && confirmingOff && (
                <div className="rounded-md border border-danger-500/30 bg-danger-50 p-3 text-[12.5px] text-danger-600">
                  The patient will no longer be able to open the link. This cannot be undone, but
                  you can create a new one.
                </div>
              )}
            </div>
          )
        )}

        <DialogFooter className="flex-wrap gap-2">
          {showForm ? (
            <Button onClick={() => void create()} disabled={creating || emailBad}>
              {emailValid ? <Mail /> : null}
              {creating
                ? 'Creating…'
                : emailValid
                  ? 'Email link to patient'
                  : 'Create patient link'}
            </Button>
          ) : patientLink?.active ? (
            <>
              <Button
                variant="outline"
                className="text-danger-500 hover:text-danger-600"
                onClick={() => {
                  if (confirmingOff) {
                    void onTurnOff().then(() => {
                      setConfirmingOff(false);
                    });
                  } else {
                    setConfirmingOff(true);
                  }
                }}
              >
                <Link2Off />
                {confirmingOff ? 'Yes, turn it off' : 'Turn off link'}
              </Button>
              <Button variant="outline" asChild>
                <a href={patientLink.url} target="_blank" rel="noreferrer">
                  <ExternalLink />
                  Preview what they see
                </a>
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                setReplacing(true);
              }}
            >
              Create a new link
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
